// The browser half of the session recorder (server: services/sessionRecorder.ts).
//
// One recorder per tab, kept outside React so it survives navigation: it
// captures the microphone (and, on request, the sound of an online meeting
// shared from another tab or window), cuts the audio into 5-second pieces and
// sends them to PMO in order. A piece that fails is retried until it gets
// through; nothing is dropped while the tab stays open.

const CHUNK_MS = 5000;

export type RecorderPhase = 'idle' | 'starting' | 'recording' | 'paused' | 'stopping';
export type AudioSource = 'room' | 'online';

export interface RecorderState {
  phase: RecorderPhase;
  recordingId: number | null;
  projectId: number | null;
  title: string | null;
  audioSource: AudioSource;
  /** ms recorded so far, pauses excluded (add Date.now() - runningSince while recording). */
  elapsedMs: number;
  runningSince: number | null;
  /** pieces recorded but not yet stored by PMO */
  pending: number;
  /** last upload failed: still retrying */
  offline: boolean;
  /** the shared meeting sound stopped (the user ended the sharing): recording goes on with the microphone */
  onlineAudioLost: boolean;
  error: string | null;
  /** the technical reason behind `error`, shown small under the message */
  errorDetail: string | null;
  /** name of the microphone being recorded, as the browser gives it */
  micLabel: string | null;
}

const initial: RecorderState = {
  phase: 'idle', recordingId: null, projectId: null, title: null, audioSource: 'room',
  elapsedMs: 0, runningSince: null, pending: 0, offline: false, onlineAudioLost: false, error: null, errorDetail: null, micLabel: null,
};

let state: RecorderState = { ...initial };
const listeners = new Set<(s: RecorderState) => void>();

function set(patch: Partial<RecorderState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l(state);
}

export function getRecorderState(): RecorderState { return state; }
export function subscribeRecorder(l: (s: RecorderState) => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

// ── Capture ──

let media: MediaRecorder | null = null;
let streams: MediaStream[] = [];
let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let micTrack: MediaStreamTrack | null = null;
let lastStart: StartOptions | null = null;

/** The analyser on what is being recorded, for the sound display; null when the browser gave no audio context. */
export function getRecorderAnalyser(): AnalyserNode | null { return analyser; }
let heartbeatTimer: ReturnType<typeof setInterval> | undefined;

function pickMime(): string {
  const options = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  for (const m of options) if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  return '';
}

export function recorderSupported(): boolean {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

/** Sharing a tab's or screen's sound works in Chrome and Edge (not Safari, not Firefox). */
export function onlineAudioSupported(): boolean {
  return !!(navigator.mediaDevices as any)?.getDisplayMedia && /Chrome|Edg\//.test(navigator.userAgent);
}

// ── Microphone choice ──

const MIC_KEY = 'pmo.recorder.mic';

export function getPreferredMic(): string | null {
  try { return localStorage.getItem(MIC_KEY); } catch { return null; }
}
export function setPreferredMic(deviceId: string | null) {
  try { deviceId ? localStorage.setItem(MIC_KEY, deviceId) : localStorage.removeItem(MIC_KEY); } catch { /* not remembered */ }
}

export interface Microphone { deviceId: string; label: string }

/** The microphones the browser offers. Names only come once the site may use the microphone. */
export async function listMicrophones(): Promise<Microphone[]> {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all
      .filter(d => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'communications' && d.label)
      .map(d => ({ deviceId: d.deviceId, label: d.label }));
  } catch { return []; }
}

const VOICE = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

async function captureStream(source: AudioSource, deviceId: string | null): Promise<MediaStream> {
  step = 'microphone';
  let mic: MediaStream;
  try {
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { ...VOICE, deviceId: { exact: deviceId } } : VOICE });
    } catch (err: any) {
      // The chosen microphone is gone (unplugged…): forget it and take the default one.
      if (!deviceId || !(err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError')) throw err;
      setPreferredMic(null);
      mic = await navigator.mediaDevices.getUserMedia({ audio: VOICE });
    }
  } catch (err: any) {
    // A refusal is final; anything else (a constraint the device can't meet…) gets a plain retry.
    if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') throw err;
    mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  }
  streams.push(mic);
  micTrack = mic.getAudioTracks()[0] || null;
  set({ micLabel: micTrack?.label || null });
  step = 'audio-context';
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    audioCtx = new Ctx();
    // A context created outside a click can start suspended: anything routed
    // through it would then record silence. resume() may never settle: cap it.
    await Promise.race([audioCtx!.resume().catch(() => {}), new Promise(r => setTimeout(r, 1500))]);
  } catch {
    audioCtx = null;
  }

  // In the room the microphone is recorded as is; the context only feeds the meter.
  let recorded: MediaStream = mic;
  if (source === 'online') {
    let display: MediaStream;
    try {
      display = await (navigator.mediaDevices as any).getDisplayMedia({
        video: true,
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        systemAudio: 'include',
        selfBrowserSurface: 'exclude',
      });
    } catch {
      throw new Error('online_cancelled');
    }
    streams.push(display);
    const sound = display.getAudioTracks()[0];
    if (!sound) throw new Error('online_no_audio');
    if (!audioCtx || audioCtx.state !== 'running') throw Object.assign(new Error('audio mixer not running'), { name: 'MixerError' });
    // Microphone + meeting sound mixed into one track.
    const dest = audioCtx.createMediaStreamDestination();
    audioCtx.createMediaStreamSource(mic).connect(dest);
    audioCtx.createMediaStreamSource(new MediaStream([sound])).connect(dest);
    sound.addEventListener('ended', () => set({ onlineAudioLost: true }));
    recorded = dest.stream;
  }

  // Analyser for the sound display — optional: no display is no reason not to record.
  try {
    if (audioCtx) {
      const a = audioCtx.createAnalyser();
      a.fftSize = 1024;
      a.smoothingTimeConstant = 0.7;
      a.minDecibels = -85;
      a.maxDecibels = -25;
      audioCtx.createMediaStreamSource(recorded).connect(a);
      analyser = a;
    }
  } catch { analyser = null; }

  return recorded;
}

function release() {
  analyser = null;
  micTrack = null;
  clearInterval(heartbeatTimer);
  for (const s of streams) for (const t of s.getTracks()) t.stop();
  streams = [];
  void audioCtx?.close().catch(() => {});
  audioCtx = null;
  media = null;
  window.removeEventListener('beforeunload', guardUnload);
}

function guardUnload(e: BeforeUnloadEvent) {
  if (state.phase !== 'idle' || queue.length > 0) {
    e.preventDefault();
    e.returnValue = '';
  }
}

// ── Upload queue ──

let queue: { index: number; blob: Blob }[] = [];
let nextIndex = 1;
let sending = false;
let drained: (() => void) | null = null;

async function sendLoop() {
  if (sending) return;
  sending = true;
  let delay = 1000;
  try {
    while (queue.length > 0 && state.recordingId) {
      const item = queue[0];
      try {
        const res = await fetch(`/api/recordings/${state.recordingId}/chunks/${item.index}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: item.blob,
        });
        if (res.ok) {
          queue.shift();
          delay = 1000;
          set({ pending: queue.length, offline: false });
          continue;
        }
        const body = await res.json().catch(() => null);
        if (res.status === 409 && Number.isInteger(body?.expected)) {
          // PMO already has every piece before `expected`.
          queue = queue.filter(q => q.index >= body.expected);
          set({ pending: queue.length });
          continue;
        }
        if (res.status === 409 || res.status === 404) {
          // The recording was closed on the server (finished elsewhere or abandoned).
          queue = [];
          set({ pending: 0, error: 'closed' });
          break;
        }
        throw new Error(String(res.status));
      } catch {
        set({ offline: true });
        await new Promise(r => setTimeout(r, delay));
        delay = Math.min(delay * 2, 15000);
      }
    }
  } finally {
    sending = false;
    if (queue.length === 0 && drained) { drained(); drained = null; }
  }
}

function waitDrained(): Promise<void> {
  if (queue.length === 0 && !sending) return Promise.resolve();
  return new Promise(resolve => { drained = resolve; void sendLoop(); });
}

// ── Commands ──

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/recordings${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || 'Request failed'), { code: body.code });
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

/** MediaRecorder with our settings; falls back to the browser's defaults when it refuses them. */
function makeRecorder(stream: MediaStream, mimeType: string): MediaRecorder {
  const attempts: MediaRecorderOptions[] = [
    { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 64000 },
    mimeType ? { mimeType } : {},
    {},
  ];
  let last: unknown;
  for (const o of attempts) {
    try { return new MediaRecorder(stream, o); } catch (e) { last = e; }
  }
  throw last;
}

/** Tell PMO's server log what went wrong on this browser (no audio, just the facts). */
function reportClientError(detail: string, kind: 'start' | 'silence' = 'start') {
  void fetch('/api/recordings/client-error', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ detail, kind, userAgent: navigator.userAgent }),
  }).catch(() => {});
}

/** The microphone hears nothing since the start: log what the browser says about it. */
export async function reportSilence(): Promise<void> {
  const t = micTrack;
  const s: MediaTrackSettings = t?.getSettings?.() || {};
  const mics = await listMicrophones();
  const detail = [
    `mic "${t?.label || '?'}"`,
    `state ${t?.readyState || '?'}${t?.muted ? ' muted' : ''}${t && !t.enabled ? ' disabled' : ''}`,
    `rate ${s.sampleRate ?? '?'} ch ${s.channelCount ?? '?'}`,
    `ctx ${audioCtx?.state || 'none'}`,
    `inputs: ${mics.map(m => m.label).join(' / ') || '(no names)'}`,
  ].join(' | ');
  reportClientError(detail, 'silence');
}

/** Start again on another microphone. Only offered while nothing has been heard, so nothing is lost. */
export async function restartWithMicrophone(deviceId: string): Promise<void> {
  const opts = lastStart;
  if (!opts) return;
  setPreferredMic(deviceId);
  await discardRecording();
  await startRecording({ ...opts, deviceId });
}

let step = 'idle';

export interface StartOptions { projectId: number; calendarEventId: number | null; audioSource: AudioSource; deviceId?: string | null }

export async function startRecording(opts: StartOptions): Promise<void> {
  if (state.phase !== 'idle') return;
  lastStart = opts;
  set({ ...initial, phase: 'starting', projectId: opts.projectId, audioSource: opts.audioSource });
  let createdId: number | null = null;
  try {
    const stream = await captureStream(opts.audioSource, opts.deviceId === undefined ? getPreferredMic() : opts.deviceId);
    step = 'recorder';
    const mimeType = pickMime();
    media = makeRecorder(stream, mimeType);
    const actualMime = (media.mimeType || mimeType || 'audio/webm');
    step = 'pmo';
    const rec = await api<{ id: number; title: string }>('', {
      method: 'POST',
      body: JSON.stringify({ projectId: opts.projectId, calendarEventId: opts.calendarEventId, audioSource: opts.audioSource, mimeType: actualMime }),
    });
    createdId = rec.id;
    step = 'start';
    queue = [];
    nextIndex = 1;
    media.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      queue.push({ index: nextIndex++, blob: e.data });
      set({ pending: queue.length });
      void sendLoop();
    };
    try {
      media.start(CHUNK_MS);
    } catch {
      // Some browsers accept the settings at creation and refuse them at start.
      const handler = media.ondataavailable;
      media = new MediaRecorder(stream);
      media.ondataavailable = handler;
      media.start(CHUNK_MS);
    }
    window.addEventListener('beforeunload', guardUnload);
    heartbeatTimer = setInterval(() => {
      if (state.recordingId) void fetch(`/api/recordings/${state.recordingId}/heartbeat`, { method: 'POST', credentials: 'include' }).catch(() => {});
    }, 60_000);
    set({ phase: 'recording', recordingId: rec.id, title: rec.title, runningSince: Date.now() });
  } catch (err: any) {
    release();
    const msg = err?.name === 'NotAllowedError' ? 'mic_denied'
      : err?.name === 'NotFoundError' ? 'no_mic'
      : err?.name === 'NotReadableError' ? 'mic_busy'
      : err?.message === 'online_no_audio' || err?.message === 'online_cancelled' ? err.message
      : err?.code === 'no_speakr' ? 'no_speakr' : 'start_failed';
    const detail = `${step}: ${err?.name || 'Error'}${err?.message ? ` — ${String(err.message).slice(0, 160)}` : ''}`;
    console.error('Recorder could not start', step, err);
    reportClientError(detail);
    if (createdId) void api(`/${createdId}`, { method: 'DELETE' }).catch(() => {});
    set({ ...initial, error: msg, errorDetail: detail });
  }
}

export function pauseRecording() {
  if (state.phase !== 'recording' || !media) return;
  media.pause();
  set({ phase: 'paused', elapsedMs: state.elapsedMs + (Date.now() - (state.runningSince || Date.now())), runningSince: null });
}

export function resumeRecording() {
  if (state.phase !== 'paused' || !media) return;
  media.resume();
  set({ phase: 'recording', runningSince: Date.now() });
}

function stopMedia(): Promise<void> {
  return new Promise(resolve => {
    if (!media || media.state === 'inactive') return resolve();
    media.addEventListener('stop', () => resolve(), { once: true });
    media.stop();
  });
}

/** Stop and hand over: the last pieces are sent, then PMO passes the recording to Speakr. */
export async function stopRecording(): Promise<void> {
  if (state.phase !== 'recording' && state.phase !== 'paused') return;
  const id = state.recordingId;
  const elapsed = state.elapsedMs + (state.runningSince ? Date.now() - state.runningSince : 0);
  set({ phase: 'stopping', elapsedMs: elapsed, runningSince: null });
  await stopMedia();
  await waitDrained();
  release();
  try {
    if (id) await api(`/${id}/stop`, { method: 'POST' });
    set({ ...initial });
  } catch {
    set({ ...initial, error: 'stop_failed' });
  }
}

export async function discardRecording(): Promise<void> {
  const id = state.recordingId;
  queue = [];
  if (media) { media.ondataavailable = null; await stopMedia(); }
  release();
  set({ ...initial });
  if (id) await api(`/${id}`, { method: 'DELETE' }).catch(() => {});
}

export function clearRecorderError() { set({ error: null, errorDetail: null }); }

/** A recording PMO still holds open but this tab doesn't run (the tab was closed): finish or drop it. */
export async function finishOrphan(id: number): Promise<void> { await api(`/${id}/stop`, { method: 'POST' }); }
export async function discardOrphan(id: number): Promise<void> { await api(`/${id}`, { method: 'DELETE' }); }
export async function retryHandover(id: number): Promise<void> { await api(`/${id}/stop`, { method: 'POST' }); }
